import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRight, ArrowLeft, Columns3 } from 'lucide-react';
import { IMPORT_FIELDS } from '../../lib/bulkImportFields';
import { keyed } from '../../lib/listKeys';
import { SavedMappingNotice } from './SavedMappingNotice';

const DONT_IMPORT = '__skip__';

export function MapColumnsStep({ headers, sampleRow, mapping, onMappingChange, onBack, onNext, fields = IMPORT_FIELDS, savedMapping, rememberMapping, onRememberChange, ...profileProps }) {
  const usedFields = new Set(Object.values(mapping).filter(Boolean));

  const setFieldFor = (header, fieldKey) => {
    const next = { ...mapping };
    // A field can only be claimed by one column — unassign it from wherever
    // it was before, so swapping two dropdowns just works.
    if (fieldKey) {
      Object.keys(next).forEach((h) => { if (next[h] === fieldKey) next[h] = null; });
    }
    next[header] = fieldKey || null;
    onMappingChange(next);
  };

  const mappedRequiredKeys = new Set(Object.values(mapping).filter(Boolean));
  const missingRequired = fields.filter(f => f.required && !mappedRequiredKeys.has(f.key));

  return (
    <div className="space-y-6" data-testid="map-columns-step">
      <SavedMappingNotice savedMapping={savedMapping} rememberMapping={rememberMapping} onRememberChange={onRememberChange} {...profileProps} />
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Columns3 className="h-4 w-4 text-primary" /> Match your columns to DutiLyne's fields
          </CardTitle>
          <CardDescription>
            We've guessed a match for each column found in your file. Change anything that's
            wrong, or set a column to "Don't import" to leave it out.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {keyed(headers, h => h).map(({ item: header, key }) => {
              const fieldKey = mapping[header] || null;
              const field = fields.find(f => f.key === fieldKey);
              return (
                <div key={key} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 p-2.5 rounded-lg border border-border/60" data-testid={`map-row-${header}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" title={header}>{header}</p>
                    {sampleRow?.[header] != null && String(sampleRow[header]).trim() !== '' && (
                      <p className="text-xs text-muted-foreground truncate">e.g. "{String(sampleRow[header])}"</p>
                    )}
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <Select
                    value={fieldKey || DONT_IMPORT}
                    onValueChange={(v) => setFieldFor(header, v === DONT_IMPORT ? null : v)}
                  >
                    <SelectTrigger data-testid={`map-select-${header}`} className="h-9 w-full">
                      <SelectValue placeholder="Don't import" />
                    </SelectTrigger>
                    <SelectContent data-testid={`map-options-${header}`}>
                      <SelectItem value={DONT_IMPORT} data-testid={`map-option-${header}-skip`}>Don't import</SelectItem>
                      {fields.map((f) => (
                        <SelectItem key={f.key} value={f.key} data-testid={`map-option-${header}-${f.key}`}>
                          {f.label}{f.required ? ' *' : ''}{usedFields.has(f.key) && f.key !== fieldKey ? ' (mapped elsewhere)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {field?.required && <Badge className="text-[10px] col-start-3 w-fit -mt-1 justify-self-start">Required field</Badge>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {missingRequired.length > 0 && (
        <p className="text-sm text-amber-600 dark:text-amber-400 text-right" data-testid="map-missing-required">
          Still need to map: {missingRequired.map(f => f.label).join(', ')}
        </p>
      )}

      <div className="flex justify-between">
        <Button variant="outline" className="gap-1.5" onClick={onBack} data-testid="map-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button className="gap-1.5" onClick={onNext} disabled={missingRequired.length > 0 || (rememberMapping && !profileProps.profileName?.trim())} data-testid="map-next-btn">
          Next: Preview <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
